import { useState, type Dispatch, type SetStateAction } from "react";
import { observer } from "mobx-react-lite";
import { requestProcessingCompletionNudge } from "@/components/chat/SidebarChat/chatNudgeStore";
import { useUploadQueueStore } from "@/hooks/useUploadQueueStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { DocumentUploadQueueTranslations } from "@/lib/i18n/types/documents-sections/upload";
import {
  ActiveUploadStatusBubble,
  MinimizedStatusBubble,
  ProcessingStatusBubble,
  StatusControls,
  type Segment,
} from "./UploadQueueStatusBubbles";

interface UploadQueueProgressProps {
  buildingId: string;
  /** Count of documents currently processing (from DocumentStore). */
  processingCount?: number;
}

function useUploadStatusUi({
  hasUploads,
  isComplete,
  totalCount,
  currentProcessingTotal,
  latestBatchId,
}: {
  hasUploads: boolean;
  isComplete: boolean;
  totalCount: number;
  currentProcessingTotal: number;
  latestBatchId: string | undefined;
}) {
  const [isMinimized, setIsMinimized] = useState(false);
  const [dismissedAtBatch, setDismissedAtBatch] = useState<string | undefined>();
  const [dismissedAtTotal, setDismissedAtTotal] = useState<number | null>(null);
  const [subscribedAtTotal, setSubscribedAtTotal] = useState<number | null>(null);
  const [initialProcessingTotal] = useState(currentProcessingTotal);
  const processingTotal = Math.max(initialProcessingTotal, currentProcessingTotal);
  const hasNewBatch = Boolean(latestBatchId && latestBatchId !== dismissedAtBatch);
  const hasNewUploads =
    hasUploads && (hasNewBatch || (!isComplete && totalCount > (dismissedAtTotal ?? totalCount)));
  const isDismissed = dismissedAtTotal !== null && !hasNewUploads;
  const isCompletionSubscribed = subscribedAtTotal === processingTotal;

  return {
    isMinimized,
    setIsMinimized,
    isDismissed,
    setDismissedAtTotal: (total: SetStateAction<number | null>) => {
      setDismissedAtTotal(total);
      setDismissedAtBatch(latestBatchId);
    },
    isCompletionSubscribed,
    markCompletionSubscribed: () => setSubscribedAtTotal(processingTotal),
    processingTotal,
  };
}

function uploadMessage(
  isComplete: boolean,
  hasFailures: boolean,
  completed: number,
  total: number,
  copy: Pick<
    DocumentUploadQueueTranslations,
    "receivedFiles" | "receivedFilesWithFailures" | "receivingFiles"
  >
): string {
  if (!isComplete) return copy.receivingFiles;
  if (!hasFailures) return copy.receivedFiles;
  return copy.receivedFilesWithFailures
    .replace("{completed}", String(completed))
    .replace("{total}", String(total));
}

function processingMessage(
  total: number,
  copy: Pick<DocumentUploadQueueTranslations, "processingMessage" | "processingMessageOne">
): string {
  return total === 1
    ? copy.processingMessageOne
    : copy.processingMessage.replace("{count}", String(total));
}

function percent(completed: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((completed / total) * 100);
}

type UploadQueueStore = ReturnType<typeof useUploadQueueStore>;

function positive(value: number): boolean {
  return value > 0;
}

function processingTotalNow(hasUploads: boolean, totalCount: number, processingCount: number) {
  return Math.max(processingCount, hasUploads ? totalCount : 0);
}

function completedProcessingInputs(
  hasUploads: boolean,
  completedUploads: number,
  processingTotal: number
): number {
  return hasUploads ? completedUploads : processingTotal;
}

function isProcessingStage(
  hasUploads: boolean,
  hasProcessing: boolean,
  isComplete: boolean,
  hasFailures: boolean
): boolean {
  if (!hasProcessing) return false;
  if (!hasUploads) return true;
  return isComplete && !hasFailures;
}

function uploadSegmentsFor(uploadStore: UploadQueueStore): Segment[] {
  return [
    { count: uploadStore.completedCount, className: "bg-success" },
    { count: uploadStore.uploadingCount, className: "bg-primary" },
    { count: uploadStore.pendingCount, className: "bg-neutral-300" },
    { count: uploadStore.failedCount, className: "bg-destructive" },
  ];
}

function processingStatusFor(
  uploadStore: UploadQueueStore,
  hasUploads: boolean,
  hasProcessing: boolean,
  processingCount: number,
  storedProcessingTotal: number,
  currentProcessingTotal: number
) {
  const effectiveProcessingTotal = Math.max(storedProcessingTotal, currentProcessingTotal);
  const processingInputs = completedProcessingInputs(
    hasUploads,
    uploadStore.completedCount,
    effectiveProcessingTotal
  );
  const hasFailures = positive(uploadStore.failedCount);
  return {
    effectiveProcessingTotal,
    processedCount: Math.max(0, processingInputs - processingCount),
    hasFailures,
    showProcessing: isProcessingStage(
      hasUploads,
      hasProcessing,
      uploadStore.isComplete,
      hasFailures
    ),
  };
}

function replaceProgress(template: string, done: number, total: number): string {
  return template.replace("{done}", String(done)).replace("{total}", String(total));
}

interface StatusActionInputs {
  uploadStore: UploadQueueStore;
  buildingId: string;
  hasUploads: boolean;
  isComplete: boolean;
  processingTotal: number;
  setIsMinimized: Dispatch<SetStateAction<boolean>>;
  setDismissedAtTotal: Dispatch<SetStateAction<number | null>>;
  markCompletionSubscribed: () => void;
}

function createStatusActions(inputs: StatusActionInputs) {
  const {
    uploadStore,
    buildingId,
    hasUploads,
    isComplete,
    processingTotal,
    setIsMinimized,
    setDismissedAtTotal,
    markCompletionSubscribed,
  } = inputs;
  const dismiss = () => {
    setDismissedAtTotal(uploadStore.totalCount);
    if (hasUploads && isComplete) uploadStore.reset();
  };
  const retryFailed = () => {
    uploadStore.retryFailed();
    uploadStore.startProcessing();
  };
  const subscribeToCompletion = () => {
    const subscribed = requestProcessingCompletionNudge(buildingId, processingTotal);
    if (subscribed) markCompletionSubscribed();
  };
  return {
    dismiss,
    retryFailed,
    subscribeToCompletion,
    cancel: () => uploadStore.cancelAll(),
    toggle: () => setIsMinimized((value) => !value),
  };
}

function useUploadStatusController(buildingId: string, processingCount: number) {
  const uploadStore = useUploadQueueStore();
  const { t } = useLanguage();
  const copy = t.documents.uploadQueue;
  const hasUploads = positive(uploadStore.totalCount);
  const hasProcessing = positive(processingCount);
  const currentProcessingTotal = processingTotalNow(
    hasUploads,
    uploadStore.totalCount,
    processingCount
  );
  const ui = useUploadStatusUi({
    hasUploads,
    isComplete: uploadStore.isComplete,
    totalCount: uploadStore.totalCount,
    currentProcessingTotal,
    latestBatchId: Array.from(uploadStore.storageBatchFiles?.values() ?? []).at(-1)?.batchId,
  });
  const processing = processingStatusFor(
    uploadStore,
    hasUploads,
    hasProcessing,
    processingCount,
    ui.processingTotal,
    currentProcessingTotal
  );
  const segments = uploadSegmentsFor(uploadStore);
  const actions = createStatusActions({
    uploadStore,
    buildingId,
    hasUploads,
    isComplete: uploadStore.isComplete,
    processingTotal: processing.effectiveProcessingTotal,
    ...ui,
  });
  return {
    uploadStore,
    copy,
    processingCount,
    hasUploads,
    hasProcessing,
    segments,
    actions,
    ...processing,
    ...ui,
  };
}

type UploadStatusController = ReturnType<typeof useUploadStatusController>;

function processingProgressFor(status: UploadStatusController) {
  const completed = status.hasUploads ? status.uploadStore.completedCount : status.processedCount;
  const total = status.hasUploads ? status.uploadStore.totalCount : status.effectiveProcessingTotal;
  return {
    completed,
    total,
    percent: status.hasUploads ? status.uploadStore.overallProgress : percent(completed, total),
    segments: status.hasUploads
      ? status.segments
      : [
          { count: completed, className: "bg-success" },
          { count: status.processingCount, className: "bg-info" },
        ],
  };
}

function UploadStatusControls({ status }: { status: UploadStatusController }) {
  return (
    <StatusControls
      isMinimized={status.isMinimized}
      onToggle={status.actions.toggle}
      onDismiss={status.actions.dismiss}
      expandLabel={status.copy.expand}
      minimizeLabel={status.copy.minimize}
      dismissLabel={status.copy.dismiss}
    />
  );
}

function ExpandedStatusBubble({ status }: { status: UploadStatusController }) {
  const controls = <UploadStatusControls status={status} />;
  const processingDetail = status.hasProcessing
    ? replaceProgress(
        status.copy.extractingProgress,
        status.processedCount,
        status.effectiveProcessingTotal
      )
    : null;

  if (status.showProcessing) {
    const progress = processingProgressFor(status);
    return (
      <ProcessingStatusBubble
        message={processingMessage(status.effectiveProcessingTotal, status.copy)}
        controls={controls}
        progressCompleted={progress.completed}
        progressTotal={progress.total}
        progressPercent={progress.percent}
        segments={progress.segments}
        processingDetail={processingDetail ?? ""}
        subscribed={status.isCompletionSubscribed}
        copy={status.copy}
        onSubscribe={status.actions.subscribeToCompletion}
      />
    );
  }

  return (
    <ActiveUploadStatusBubble
      message={uploadMessage(
        status.uploadStore.isComplete,
        status.hasFailures,
        status.uploadStore.completedCount,
        status.uploadStore.totalCount,
        status.copy
      )}
      controls={controls}
      uploadStore={status.uploadStore}
      segments={status.segments}
      copy={status.copy}
      hasFailures={status.hasFailures}
      processingDetail={processingDetail}
      onRetry={status.actions.retryFailed}
      onCancel={status.actions.cancel}
    />
  );
}

const ActiveUploadQueueProgress = observer(function ActiveUploadQueueProgress({
  buildingId,
  processingCount = 0,
}: UploadQueueProgressProps) {
  const status = useUploadStatusController(buildingId, processingCount);

  if (status.isDismissed) return null;
  if (status.isMinimized) {
    return (
      <MinimizedStatusBubble
        showProcessing={status.showProcessing}
        processingCount={status.processingCount}
        overallProgress={status.uploadStore.overallProgress}
        controls={<UploadStatusControls status={status} />}
        copy={status.copy}
      />
    );
  }
  return <ExpandedStatusBubble status={status} />;
});

/** Upload and pipeline progress, presented as Franklin speaking above the chat pill. */
export const UploadQueueProgress = observer(function UploadQueueProgress({
  buildingId,
  processingCount = 0,
}: UploadQueueProgressProps) {
  const uploadStore = useUploadQueueStore();
  const hasUploads = positive(uploadStore.totalCount);
  const hasProcessing = positive(processingCount);

  if (!hasUploads && !hasProcessing) return null;
  return <ActiveUploadQueueProgress buildingId={buildingId} processingCount={processingCount} />;
});
